import { defineStore } from 'pinia';
import { getCache, getCacheInterval, setCache } from '@/helper/cache';
import { getDonationData } from '@/api/donation';
const donationDataRateLimitInterval = 60000; // 10 minute in milliseconds
export const CAMPAIGN_CONTENT_IDS = {
    PART_2: 2,
    PART_3: 3,
};
export const CAROUSEL_DIR = '../donation-campaign/';
// Helper to get donation information by project id or null if not found
const getDonationInfoByProjectId = (donationData, projectKey) => {
    const projectId = donationData?.[projectKey];
    if (projectId === undefined)
        return null;
    return donationData?.donationInformation?.find((info) => info.projectId === projectId) || null;
};
export const useDonationStore = defineStore('donation', {
    state: () => ({
        donationData: {},
        fetching: {
            donationData: undefined,
        },
    }),
    getters: {
        showDonationModal: (state) => state.donationData?.showDonationModal || false,
        showDonationModalInHoursForLoggedInUsers: (state) => state.donationData?.showDonationModalInHoursForLoggedInUsers || 0,
        showDonationModalInHoursForLoggedOutUsers: (state) => state.donationData?.showDonationModalInHoursForLoggedOutUsers || 0,
        showCampaignCard: (state) => state.donationData?.showCampaignCard || false,
        showDonationCampaignPart1: (state) => state.donationData?.showDonationCampaignPart1 || false,
        showDonationCampaignPart2: (state) => state.donationData?.showDonationCampaignPart2 || false,
        showDonationCampaignGallery: (state) => state.donationData?.showDonationCampaignGallery || false,
        iframeOneTimeUrl: (state) => state.donationData?.iframeOneTimeUrl || '',
        iframeFriendshipCircleUrl: (state) => state.donationData?.iframeFriendshipCircleUrl || '',
        iframeCampaignUrl: (state) => state.donationData?.iframeCampaignUrl || '',
        iframeSelfserviceUrl: (state) => state.donationData?.iframeSelfserviceUrl || '',
        donationModalInfoUrl: (state) => state.donationData?.donationModalInfoUrl || '',
        donationModalPopupUrl: (state) => state.donationData?.donationModalPopupUrl || '',
        twingleEmbedConfig: (state) => {
            return {
                campaign: { url: state.donationData.iframeCampaignUrl ?? '', containerType: 'script' },
                friendship_circle: { url: state.donationData.iframeFriendshipCircleUrl ?? '', containerType: 'script' },
                onetime: { url: state.donationData.iframeOneTimeUrl ?? '', containerType: 'script' },
                selfservice: { url: state.donationData.iframeSelfserviceUrl ?? '', containerType: 'iframe' },
            };
        },
        campaignDonationInfo: (state) => getDonationInfoByProjectId(state.donationData, 'campaignId'),
        friendshipCircleDonationInfo: (state) => getDonationInfoByProjectId(state.donationData, 'friendshipCircleId'),
        oneTimeDonationInfo: (state) => getDonationInfoByProjectId(state.donationData, 'oneTimeDonationId'),
        donationModalInfo: (state) => getDonationInfoByProjectId(state.donationData, 'donationModalId'),
        friendshipCircleDonators: (state) => getDonationInfoByProjectId(state.donationData, 'friendshipCircleId')?.donationProjectStatus?.donators || 0,
        oneTimeDonators: (state) => getDonationInfoByProjectId(state.donationData, 'oneTimeDonationId')?.donationProjectStatus?.donators || 0,
        donationModalDonators: (state) => getDonationInfoByProjectId(state.donationData, 'donationModalId')?.donationProjectStatus?.donators || 0,
    },
    actions: {
        async fetchDonationData(force = false) {
            if (this.fetching.donationData)
                return this.fetching.donationData;
            let resolver = () => { };
            this.fetching.donationData = new Promise((resolve) => {
                resolver = resolve;
            });
            const cacheRequestName = 'donationData';
            try {
                if (force || (await getCacheInterval(cacheRequestName, donationDataRateLimitInterval))) {
                    this.donationData = await getDonationData();
                    await setCache(cacheRequestName, this.donationData);
                }
                else {
                    this.donationData = await getCache(cacheRequestName);
                }
            }
            catch (e) {
                console.error('Error fetching donation data:', e);
            }
            delete this.fetching.donationData;
            resolver();
        },
    },
});
